import * as Sentry from '@sentry/react';

/**
 * Client-side Sentry init. Call from main.jsx BEFORE React renders so
 * boot errors get captured too.
 *
 * The DSN is public-by-design (it identifies the project, doesn't
 * authorize anything destructive) — Sentry's threat model assumes DSNs
 * are visible in production JS. Spam/abuse is rate-limited on their end.
 * Same reason Firebase apiKey is safe to commit.
 *
 * Skips init in development (localhost) — keeps the Sentry project
 * clean and avoids spurious events during local debugging.
 */

const CLIENT_DSN = 'https://64d2f1c85fa0d2f280406979cd93d0d9@o4511440397598720.ingest.us.sentry.io/4511440404217856';

export function initSentry() {
  if (typeof window === 'undefined') return; // SSR safety
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocal) return;

  Sentry.init({
    dsn: CLIENT_DSN,
    environment: window.location.hostname.includes('gearbox-prod') ? 'prod' : 'staging',
    // Release tag = commit SHA baked at build time by vite.config.js. Enables
    // Sentry's "first seen in release X" + auto-resolve-on-newer-release flow
    // + powers the inbox's regression detection via lastSeenInCommit.
    release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    tracesSampleRate: 0,        // Performance tracing off for now (cost)
    replaysSessionSampleRate: 0, // Session replay off for now (cost + privacy)
    replaysOnErrorSampleRate: 0,
    beforeSend: scrubEvent,
    // Network noise we don't care about — every fitness PWA gets these,
    // and each one is handled by something else (SDK retry, app reload, etc.)
    // so the Sentry event would be telemetry with no possible action.
    // Filtering at the source keeps the dashboard tile signal-only.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Service worker noise on first-time install
      /^Failed to register a ServiceWorker/i,
      // Stale-chunk-after-deploy: handled invisibly by lazyWithRetry which
      // catches the import failure, shows a brief "Updating…" banner, and
      // reloads the page with the new chunks. The user never sees a broken
      // state — Sentry doesn't need to see one either. Three browser
      // variants of the same root cause:
      /text\/html.*not a valid JavaScript MIME type/i,
      /Failed to fetch dynamically imported module/i,
      /Importing a module script failed/i,
      // Firestore SDK transient offline: retried automatically when the
      // connection restores. Real Firestore outages would surface as auth +
      // write + listener errors too, so filtering this one specific
      // benign variant doesn't blind us to genuine infrastructure issues.
      /Failed to get document because the client is offline/i,
    ],
  });
}

/**
 * Strip PII + secrets before sending to Sentry. Three layers:
 *  - user info: keep UID + gymId + role; never email/name
 *  - error message: redact anything matching OpenAI keys or JWTs
 *  - breadcrumbs: drop body/headers from fetch breadcrumbs
 *
 * Exported for unit testing.
 */
export function scrubEvent(event) {
  // 1. User identity
  if (event.user) {
    event.user = {
      id: event.user.id,
      // segment / gymId / role come in as Sentry tags, kept
    };
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  // 2. Message + exception value redaction
  const redact = (s) => {
    if (typeof s !== 'string') return s;
    return s
      .replace(/sk-[A-Za-z0-9-_]{20,}/g, 'sk-***REDACTED***') // OpenAI keys
      .replace(/eyJ[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/g, '***JWT-REDACTED***'); // JWTs
  };
  if (event.message) event.message = redact(event.message);
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = redact(ex.value);
    }
  }

  // 3. Breadcrumbs: drop bodies + auth headers from network breadcrumbs
  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (b.category === 'fetch' || b.category === 'xhr') {
        if (b.data) {
          delete b.data.body;
          delete b.data.response_body;
          if (b.data.headers) {
            delete b.data.headers.Authorization;
            delete b.data.headers.authorization;
          }
        }
      }
    }
  }

  return event;
}

/**
 * Attach UID + gym + role to every subsequent event. Call after auth
 * resolves (from AuthProvider).
 */
export function setSentryContext({ uid, gymId, role }) {
  if (typeof window === 'undefined') return;
  Sentry.setUser({ id: uid || null });
  Sentry.setTags({
    gymId: gymId || 'none',
    role: role || 'none',
  });
}

export function clearSentryContext() {
  if (typeof window === 'undefined') return;
  Sentry.setUser(null);
  Sentry.setTags({ gymId: 'none', role: 'none' });
}
